import axiosWithAuth from "../utils/axiosWithAuth";
import { backUrl } from "../utils/backURL";
const API_URL = backUrl;

/**
 * Get comprehensive dashboard data including budgets, purchase orders, and cost center allocations
 * @param {string} clientId - ID del cliente
 * @param {Object} filters - Filtros opcionales
 * @returns {Promise<Object>} - Respuesta con todos los datos del dashboard
 */
export const getDashboardData = async (clientId, filters = {}) => {
  try {
    const params = new URLSearchParams();
    
    // Agregar filtros opcionales
    if (filters.start_date) params.append('start_date', filters.start_date);
    if (filters.end_date) params.append('end_date', filters.end_date);
    if (filters.cost_center_id) params.append('cost_center_id', filters.cost_center_id);
    if (filters.status) params.append('status', filters.status);
    
    const queryString = params.toString();
    const url = `${API_URL}/workflows/p2p/${clientId}/dashboard-data${queryString ? `?${queryString}` : ''}`;
    
    const response = await axiosWithAuth.get(url);
    return {
      success: true,
      data: response.data.data || response.data,
      count: response.data.count || 0
    };
  } catch (error) {
    console.error("Error getting dashboard data:", error);
    return {
      success: false,
      error: error.response?.data?.message || "Error al obtener datos del dashboard",
      status: error.response?.status
    };
  }
};

/**
 * Get budgets with remaining amounts calculated
 * @param {string} clientId - ID del cliente
 * @param {Object} filters - Filtros opcionales
 * @returns {Promise<Object>} - Respuesta con presupuestos y montos restantes
 */
export const getBudgetsWithRemaining = async (clientId, filters = {}) => {
  try {
    const params = new URLSearchParams();
    
    if (filters.status) params.append('status', filters.status);
    if (filters.cost_center_id) params.append('cost_center_id', filters.cost_center_id);
    if (filters.start_date) params.append('start_date', filters.start_date);
    if (filters.end_date) params.append('end_date', filters.end_date);
    
    const queryString = params.toString();
    const url = `${API_URL}/workflows/p2p/${clientId}/budgets/with-remaining${queryString ? `?${queryString}` : ''}`;
    
    const response = await axiosWithAuth.get(url);
    return {
      success: true,
      data: response.data.data || response.data,
      count: response.data.count || 0
    };
  } catch (error) {
    console.error("Error getting budgets with remaining amounts:", error);
    return {
      success: false,
      error: error.response?.data?.message || "Error al obtener presupuestos",
      status: error.response?.status
    };
  }
};

/**
 * Get purchase orders with cost center allocation data
 * @param {string} clientId - ID del cliente
 * @param {Object} filters - Filtros opcionales
 * @returns {Promise<Object>} - Respuesta con órdenes de compra y asignaciones
 */
export const getPurchaseOrdersWithAllocations = async (clientId, filters = {}) => {
  try {
    const params = new URLSearchParams();
    
    if (filters.status) params.append('status', filters.status);
    if (filters.supplier_id) params.append('supplier_id', filters.supplier_id);
    if (filters.start_date) params.append('start_date', filters.start_date);
    if (filters.end_date) params.append('end_date', filters.end_date);
    if (filters.cost_center_id) params.append('cost_center_id', filters.cost_center_id);
    
    const queryString = params.toString();
    const url = `${API_URL}/workflows/p2p/${clientId}/purchase-orders/with-allocations${queryString ? `?${queryString}` : ''}`;
    
    const response = await axiosWithAuth.get(url);
    return {
      success: true,
      data: response.data.data || response.data,
      count: response.data.count || 0
    };
  } catch (error) {
    console.error("Error getting purchase orders with allocations:", error);
    return {
      success: false,
      error: error.response?.data?.message || "Error al obtener órdenes de compra",
      status: error.response?.status
    };
  }
};

/**
 * Get cost center allocation summary
 * @param {string} clientId - ID del cliente
 * @param {Object} filters - Filtros opcionales
 * @returns {Promise<Object>} - Respuesta con resumen de asignaciones
 */
export const getCostCenterAllocationSummary = async (clientId, filters = {}) => {
  try {
    const params = new URLSearchParams();
    
    if (filters.entity_type) params.append('entity_type', filters.entity_type);
    if (filters.start_date) params.append('start_date', filters.start_date);
    if (filters.end_date) params.append('end_date', filters.end_date);
    
    const queryString = params.toString();
    const url = `${API_URL}/workflows/p2p/${clientId}/cost-center-allocations/summary${queryString ? `?${queryString}` : ''}`;
    
    const response = await axiosWithAuth.get(url);
    return {
      success: true,
      data: response.data.data || response.data,
      count: response.data.count || 0
    };
  } catch (error) {
    console.error("Error getting cost center allocation summary:", error);
    return {
      success: false,
      error: error.response?.data?.message || "Error al obtener resumen de asignaciones",
      status: error.response?.status
    };
  }
};

/**
 * Get budget execution vs allocation data
 * @param {string} clientId - ID del cliente
 * @param {Object} filters - Filtros opcionales
 * @returns {Promise<Object>} - Respuesta con datos de ejecución vs asignación
 */
export const getBudgetExecutionData = async (clientId, filters = {}) => {
  try {
    const params = new URLSearchParams();
    
    if (filters.cost_center_id) params.append('cost_center_id', filters.cost_center_id);
    if (filters.start_date) params.append('start_date', filters.start_date);
    if (filters.end_date) params.append('end_date', filters.end_date);
    
    const queryString = params.toString();
    const url = `${API_URL}/workflows/p2p/${clientId}/budgets/execution-data${queryString ? `?${queryString}` : ''}`;
    
    const response = await axiosWithAuth.get(url);
    return {
      success: true,
      data: response.data.data || response.data,
      count: response.data.count || 0
    };
  } catch (error) {
    console.error("Error getting budget execution data:", error);
    return {
      success: false,
      error: error.response?.data?.message || "Error al obtener datos de ejecución",
      status: error.response?.status
    };
  }
};

/**
 * Get monthly budget vs actual spending trends
 * @param {string} clientId - ID del cliente
 * @param {Object} filters - Filtros opcionales
 * @returns {Promise<Object>} - Respuesta con tendencias mensuales
 */
export const getMonthlyBudgetTrends = async (clientId, filters = {}) => {
  try {
    const params = new URLSearchParams();
    
    if (filters.cost_center_id) params.append('cost_center_id', filters.cost_center_id);
    if (filters.months) params.append('months', filters.months);
    if (filters.year) params.append('year', filters.year);
    
    const queryString = params.toString();
    const url = `${API_URL}/workflows/p2p/${clientId}/budgets/monthly-trends${queryString ? `?${queryString}` : ''}`;
    
    const response = await axiosWithAuth.get(url);
    return {
      success: true,
      data: response.data.data || response.data,
      count: response.data.count || 0
    };
  } catch (error) {
    console.error("Error getting monthly budget trends:", error);
    return {
      success: false,
      error: error.response?.data?.message || "Error al obtener tendencias mensuales",
      status: error.response?.status
    };
  }
};

/**
 * Get top spending cost centers
 * @param {string} clientId - ID del cliente
 * @param {Object} filters - Filtros opcionales
 * @returns {Promise<Object>} - Respuesta con centros de costo con mayor gasto
 */
export const getTopSpendingCostCenters = async (clientId, filters = {}) => {
  try {
    const params = new URLSearchParams();
    
    if (filters.limit) params.append('limit', filters.limit);
    if (filters.start_date) params.append('start_date', filters.start_date);
    if (filters.end_date) params.append('end_date', filters.end_date);
    if (filters.order_by) params.append('order_by', filters.order_by);
    
    const queryString = params.toString();
    const url = `${API_URL}/workflows/p2p/${clientId}/cost-centers/top-spending${queryString ? `?${queryString}` : ''}`;
    
    const response = await axiosWithAuth.get(url);
    return {
      success: true,
      data: response.data.data || response.data,
      count: response.data.count || 0
    };
  } catch (error) {
    console.error("Error getting top spending cost centers:", error);
    return {
      success: false,
      error: error.response?.data?.message || "Error al obtener centros de costo con mayor gasto",
      status: error.response?.status
    };
  }
};
